import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { getExportFilename, CsvSearchTemplateHeader } from './VehicleModelHelper.js'
import { Space, Form, Button, Upload, Col, Row, Alert, Typography, Spin } from 'antd';
import style from './VehicleCompatibilitySearchModal.module.scss';
import { ResponsiveModal } from '../../features/responsive';
import { useFuzzySearch } from '../../utils/fuzzySearch.js';
import { getFileSize } from '../../utils/files.js';

import {
	UploadOutlined,
	CloseOutlined,
	LoadingOutlined,
	CheckCircleOutlined,
	FileTextOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import { CSVLink } from 'react-csv';
import { useGrouppedVehicleCompatibility } from '../../features/vehicleModels/vehicleModelApi.js';
import { setSearchFilters } from '../../features/vehicleModels/vehicleModelFilterSlice.js';
import { useCurrentCompany } from '../../features/company/companySlice.js';
const { Text, Title, Link } = Typography;

const VehicleCompatibilitySearchModal = ({ isModalOpen, onClose, onComplete }) => {
	const { Dragger } = Upload;
	const { i18n, t } = useTranslation();
	const csvLinkRef = useRef(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [rowUploaded, setRowUploaded] = useState(undefined);
	const [isReadingFile, setIsReadingFile] = useState(false);
	const [searchedData, setSearchedData] = useState();
	const [matchedRowCount, setMatchRowCount] = useState(0);
	const isFileInvalid = selectedFile && !selectedFile?.name.endsWith(".csv");
	const searchObj = useSelector(state => state.vehicleModelFilter.searchFilter);
	const dispatch = useDispatch();
	const currentCompany = useCurrentCompany();
	const { vehicleCompatibility, isFetching } = useGrouppedVehicleCompatibility(currentCompany.id, !searchObj || !isModalOpen, {make : searchObj?.make, model : searchObj?.model, year : searchObj?.year, time : searchObj?.time});

	useEffect(() => {
		if (isModalOpen) {
			setSelectedFile(null);
			setSearchedData(undefined);
			setMatchRowCount(0);
		}
	}, [isModalOpen])

	const close = () => {
		dispatch(setSearchFilters(undefined));
		if (onClose) {
			onClose();
		}
	}

	const readFile = (file) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			setTimeout(() => {
				const content = e.target.result;
				let rows = content.split('\n');

				rows = rows.slice(1).map(i => i.replace('\r', '')).filter(i=>i && i?.trim()?.length!== 0);
				setRowUploaded(rows.length);
				
				if (rows.length !== 0) {
		            setIsReadingFile(true);
					rows = rows.slice(0, 15).map(data => {
						let targetVehicleModel = data.split(",");

						return {
							make : targetVehicleModel[0],
							model : targetVehicleModel[1],
							year : targetVehicleModel[2],
						}
					});

					dispatch(setSearchFilters({
						...{
							make: rows.map(i => i.make).filter(i=> !!i),
							model: rows.map(i => i.model).filter(i=> !!i),
							year: rows.map(i => i.year).filter(i=> !!i),
							time:new Date()
						}
					}));
				}
			}, 500);
		};

		reader.readAsText(file);
	};

	useEffect(()=>{
		if (isReadingFile && !isFetching) {
			const matchUniqueRowCount = (Array.from(new Set(vehicleCompatibility.map(i => {
				return JSON.stringify({
					make: i.make,
					model: i.model,
					yearOfManufacture: i.yearOfManufacture,
				})
			}))).map(item => JSON.parse(item)));

			setSearchedData(vehicleCompatibility);
			setMatchRowCount((matchUniqueRowCount || []).length);
			setIsReadingFile(false);
		}
	}, [isFetching, vehicleCompatibility]);
	
	return (
		<ResponsiveModal
			onClose={close}
			isModalOpen={isModalOpen}
			width={"30%"}
			title={t("VehicleModel.CsvSearch")}
			style={{ minWidth: 350 }}
			footer={null}
		>
			<CSVLink
				enclosingCharacter={``}
				data={CsvSearchTemplateHeader}
				style={{display: "none"}}
				filename={getExportFilename({
					entityName : t("VehicleModel.VehicleModelTemplate"),
					extension: ".csv"
				})}
				ref={csvLinkRef}
			></CSVLink>
			<Row>
				<Col xs={{ span: 24 }} className={"mb-2"} style={{ padding: "15px 0px" }}>
					<Space direction={"vertical"} className={style.fullWidthRow}>
						<Form.Item style={{ margin: 0 }}>
							<Alert
								description={
									<Trans
										i18nKey={'VehicleModel.CsvSearchHint'}
										components={{
											1: (
												<Link
													onClick={() => {
														csvLinkRef?.current?.link?.click()
													}}
												/>
											)
										}}
									/>
								}
								type="info"
							/>
						</Form.Item>
						<Form.Item
							name="fileName"
							style={{ margin: 0 }}
							rules={[{ required: true, message: t("ValidationErrors.DocumentRequired") }, {pattern:/\.csv$/, message: t("ValidationErrors.InvalidDocument")}]}>
							<Dragger
								showRemoveIcon={true}
								accept={".csv"}
								beforeUpload={file => {
									setSelectedFile(file);
									if (file.type === "text/csv") {
										readFile(file)
										return false;
									}
								}}
								height={'fit-content'}
								showUploadList={false}
							>
								{selectedFile && !isFileInvalid ? (
									<div className={style.centerPanel}>
										<Space style={{ flex: "1", justifyContent: "space-between", padding: "5px 10px", alignItems: "center" }}>
											<div className={style.centerPanel} style={{ flex: "0.8" }}>
												<FileTextOutlined style={{ color: "#389E0D", marginRight: 15, fontSize: 30 }} />
												<span className={style.centerPanel} style={{ flexDirection: "column" }}>
													<Text type="secondary" className={style.fullWidthRow}>{selectedFile.name}</Text>
													<Text type="secondary" className={style.fullWidthRow} style={{ textAlign: "left" }}>{getFileSize(selectedFile)}</Text>
												</span>
											</div>
											{!isReadingFile ? <div className={style.centerPanel} style={{ flex: "0.2" }}>
												<CheckCircleOutlined style={{ color: "#86B341", fontSize: 30 }} />
											</div>
												: <div style={{ flex: "0.2" }}>
													<Spin indicator={<LoadingOutlined style={{ fontSize: 30 }} />} />
												</div>}
										</Space>
									</div>
								) : (
									<div className={style.centerPanel}>
										<div style={{ flex: "1" }}>
											{t('VehicleModel.UploadCsvSearch')}&nbsp;
											<UploadOutlined />
										</div>
									</div>
								)}
							</Dragger>
							{isFileInvalid && <Text type="danger">{t("ValidationErrors.InvalidDocument")}</Text>}
							{rowUploaded === 0 && <Text type="danger">{t("ValidationErrors.NoDataFound")}</Text>}
						</Form.Item>
						{!isFileInvalid && searchedData &&
							<Form.Item style={{ textAlign: "center" }}>
								<Trans
									i18nKey={'VehicleModel.VehicleModelSearchResultSummary'}
									values={{ number: matchedRowCount, total: rowUploaded }}
									components={{ primary: <Text type="success" strong />, secondary: <Text strong /> }}
								/>
							</Form.Item>
						}
					</Space>
				</Col>
			</Row>
			<Row>
				<Col xs={{ span: 24 }}>
					<Space>
						<Button disabled={isFetching || isReadingFile || matchedRowCount === 0} onClick={() => {
							onComplete(searchedData, rowUploaded, matchedRowCount)
						}} type="primary">
							{t("Common.ContinueButton")}
						</Button>
						{(!selectedFile || isFileInvalid) &&
							<Button onClick={close}>
								{t("Common.CancelButton")}
							</Button>
						}
					</Space>
				</Col>
			</Row>
		</ResponsiveModal>
	)
}

export default VehicleCompatibilitySearchModal;